import { isDefined } from "@clipboard-health/util-ts";
import {
  type QueryFunctionContext,
  type QueryKey,
  useInfiniteQuery,
  type UseInfiniteQueryOptions,
  type UseInfiniteQueryResult,
} from "@tanstack/react-query";
import { type AxiosError } from "axios";

import { type ApiResponse, type CommonApiParams, get } from "./api";
import { type ReactQueryMeta } from "./types";

type UseQueryResponseType<ResponseSchema> = ApiResponse<ResponseSchema>["data"];

type LocallyDefinedUseQueryOptions = "meta" | "queryKey" | "queryFn";
type DisallowedUseQueryOptions = "onSuccess" | "onError" | "onSettled";

export type UseInfiniteGetQueryOptions<
  ResponseSchema,
  ErrorType = AxiosError,
  TransformedData = UseQueryResponseType<ResponseSchema>
> = Omit<
  UseInfiniteQueryOptions<UseQueryResponseType<ResponseSchema>, ErrorType, TransformedData, any>,
  LocallyDefinedUseQueryOptions | DisallowedUseQueryOptions
> &
  Pick<ReactQueryMeta, "userSuccessMessage" | "userErrorMessage">;

export type UseInfiniteGetQueryProps<
  RequestSchema,
  ResponseSchema,
  TransformedData = UseQueryResponseType<ResponseSchema>,
  TPageParameter = unknown
> = CommonApiParams<RequestSchema, ResponseSchema> &
  Omit<
    UseInfiniteQueryOptions<UseQueryResponseType<ResponseSchema>, AxiosError, TransformedData, any>,
    LocallyDefinedUseQueryOptions | DisallowedUseQueryOptions
  > &
  Pick<ReactQueryMeta, "userSuccessMessage" | "userErrorMessage"> & {
    meta: ReactQueryMeta;
    /**
     * This function is used to transform the pageParams for the next page queryParams.
     * Resulting queryParams are merged with the existing queryParams and passed to the next request
     */
    nextPageContext: (params: {
      pageParam: NonNullable<TPageParameter>;
      queryParams: Record<string, any>;
    }) => Promise<{
      queryParams?: Record<string, any> | undefined;
    }>;
  };

export function useInfiniteGetQuery<
  RequestSchema,
  ResponseSchema,
  TransformedData = UseQueryResponseType<ResponseSchema>,
  TPageParameter = unknown
>(
  props: UseInfiniteGetQueryProps<RequestSchema, ResponseSchema, TransformedData, TPageParameter>
): UseInfiniteQueryResult<TransformedData, AxiosError> {
  const {
    url,
    queryParams,
    requestSchema,
    responseSchema,
    meta,
    userSuccessMessage,
    userErrorMessage,
    getNextPageParam,
    nextPageContext,
    ...options
  } = props;

  return useInfiniteQuery<TransformedData, AxiosError, TransformedData, any>({
    ...options,
    meta: {
      ...meta,
      userSuccessMessage: userSuccessMessage ?? meta.userSuccessMessage,
      userErrorMessage: userErrorMessage ?? meta.userErrorMessage,
    },
    queryKey: [url, queryParams],
    queryFn: async ({ pageParam }: QueryFunctionContext<QueryKey, TPageParameter | undefined>) => {
      const getProps = {
        url,
        queryParams,
        requestSchema,
        responseSchema,
      };

      if (isDefined(pageParam)) {
        const { queryParams: newQueryParams } = await nextPageContext({
          pageParam: pageParam as NonNullable<TPageParameter>,
          queryParams: queryParams ?? {},
        });

        getProps.queryParams = newQueryParams;
      }

      const response = await get(getProps);

      return response.data;
    },
    getNextPageParam,
  });
}
